import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { catchError, Observable, of } from 'rxjs'
import { Backend } from 'src/app/shared/enums/enums'
import { DdcLoggerService } from '../log/ddc-logger.service'

// --- Interfaces matching iOS AiAgentService ---

export type AiTier = 'base' | 'plus' | 'pro'

export interface AiStatus {
  subscription: string
  limit: number
  period: string
  usage: number
  remaining: number
  can_send_prompt: boolean
  ai_credits: number
  allowed_model_tiers?: AiTier[]
}

export interface AiHistoryItem {
  id: number
  title: string
  created_at: string
}

export interface AiMessageAttachment {
  attached_images?: string[]
  attached_ddc_contents?: { content_type_id: number; object_id: number }[]
}

export interface AiMessage {
  id: number
  role: 'user' | 'assistant'
  content: string
  created_at: string
  attachments?: AiMessageAttachment
  attached_image?: string | null
  attached_content_type?: number | null
  attached_object_id?: number | null
  created_content_type?: string | null
  created_object_id?: number | null
  created_content?: string
}

export interface AiSession {
  id: number
  title: string
  created_at: string
  messages: AiMessage[]
}

export interface SendMessageResponse {
  message: AiMessage | string
  session_id: number
  attachments?: AiMessageAttachment
}

export interface SendMessageAttachment {
  images?: string[]
  ddc_contents?: { content_type_id: number; object_id: number }[]
}

// Keep legacy interfaces for backward compatibility
export interface LingoRequest {
  message: string
  mode: string
  attachments?: any[]
}

export interface LingoResponse {
  message: string
  type: string
  data?: any
}

@Injectable({
  providedIn: 'root',
})
export class AiService {
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
    withCredentials: true,
  }

  constructor(
    private http: HttpClient,
    private logger: DdcLoggerService,
  ) {}

  sendMessage(
    content: string,
    sessionId?: number,
    attachment?: SendMessageAttachment,
    extraParams?: Record<string, any>,
  ): Observable<SendMessageResponse> {
    this.logger.info('AI | sendMessage')
    const body: any = { message: content, ...extraParams }
    if (sessionId) {
      body.session_id = sessionId
    }
    if (attachment) {
      body.attached_ddc_contents = attachment.ddc_contents ?? []
      body.attached_images = attachment.images ?? []
    }

    return this.http
      .post<SendMessageResponse>(
        `${Backend.HOST_CONTAINER}${Backend.AI_CHAT}`,
        body,
        this.httpOptions,
      )
      .pipe(
        catchError((error: any) => {
          this.logger.error('AI | sendMessage | error: ' + (error?.error?.detail || error?.message))
          return of({
            message: 'Something went wrong. Please try again.',
            session_id: sessionId ?? 0,
          } as SendMessageResponse)
        }),
      )
  }

  getHistory(): Observable<AiHistoryItem[]> {
    this.logger.info('AI | getHistory')
    return this.http
      .get<AiHistoryItem[]>(`${Backend.HOST_CONTAINER}${Backend.AI_HISTORY}`, {
        withCredentials: true,
      })
      .pipe(
        catchError((error: any) => {
          this.logger.error('AI | getHistory | error: ' + (error?.error?.detail || error?.message))
          return of([])
        }),
      )
  }

  getSession(id: number): Observable<AiSession | null> {
    this.logger.info('AI | getSession | id: ' + id)
    return this.http
      .get<AiSession>(`${Backend.HOST_CONTAINER}${Backend.AI_HISTORY}${id}/`, {
        withCredentials: true,
      })
      .pipe(
        catchError((error: any) => {
          this.logger.error('AI | getSession | error: ' + (error?.error?.detail || error?.message))
          return of(null)
        }),
      )
  }

  getAiStatus(): Observable<AiStatus | null> {
    this.logger.info('AI | getAiStatus')
    return this.http
      .get<AiStatus>(`${Backend.HOST_CONTAINER}${Backend.AI_STATUS}`, { withCredentials: true })
      .pipe(
        catchError((error: any) => {
          this.logger.error('AI | getAiStatus | error: ' + (error?.error?.detail || error?.message))
          return of(null)
        }),
      )
  }

  improveContent(contentType: 'word' | 'pair' | 'note', contentId: number): Observable<any> {
    this.logger.info('AI | improveContent | type: ' + contentType)
    const body = {
      content_type: contentType,
      content_id: contentId,
    }

    return this.http
      .post<any>(`${Backend.HOST_CONTAINER}${Backend.AI_IMPROVE_CONTENT}`, body, this.httpOptions)
      .pipe(
        catchError((error: any) => {
          this.logger.error(
            'AI | improveContent | error: ' + (error?.error?.detail || error?.message),
          )
          return of(null)
        }),
      )
  }

  uploadImage(formData: FormData): Observable<any> {
    this.logger.info('AI | uploadImage')
    return this.http
      .post<any>(`${Backend.HOST_CONTAINER}${Backend.AI_ATTACHMENT_IMAGE}`, formData, {
        withCredentials: true,
      })
      .pipe(
        catchError((error: any) => {
          this.logger.error('AI | uploadImage | error: ' + (error?.error?.detail || error?.message))
          return of(null)
        }),
      )
  }
}
